import { StandardEvents } from '@shopify/events';
const frame = () => new Promise((resolve) => requestAnimationFrame(resolve));
const BATCH = 6;

class CollectionDiscovery extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.archive = this.closest('collection-component');
    this.results = this.archive?.querySelector('results-list');
    if (!this.results) return;
    this.records ??= new Map();
    this.groups ??= new Map();
    this.next ??= this.dataset.source;
    this.status = this.querySelector('[role="status"]');
    this.retry = this.querySelector('[data-retry]');
    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.reveals = new IntersectionObserver((entries) => {
      let index = 0;
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        this.reveals.unobserve(entry.target);
        entry.target.style.setProperty('--br-card-delay', `${index++ * 50}ms`);
        entry.target.classList.remove('br-discovery__pending');
        entry.target.classList.add('br-discovery__enter');
      }
    });
    this.feeds = new IntersectionObserver(() => this.checkFeeds(), { rootMargin: '0px 0px 300px 0px' });
    this.near = false;
    this.observer = new IntersectionObserver((entries) => {
      this.near = entries.some((entry) => entry.isIntersecting);
      if (this.near && !this.hidden && !this.loaded && !this.failed) this.start();
    }, { rootMargin: '0px 0px 500px 0px' });
    this.observer.observe(this);
    this.mutations = new MutationObserver(() => this.refresh());
    this.mutations.observe(this.results, { subtree: true, childList: true, attributes: true,
      attributeFilter: ['data-state', 'data-last-page', 'aria-busy', 'infinite-scroll'] });
    this.addEventListener('click', this.click, { signal: this.controller.signal });
    document.addEventListener('scroll', this.queueFeedCheck, { passive: true, capture: true, signal: this.controller.signal });
    document.addEventListener(StandardEvents.collectionUpdate, this.filter, { signal: this.controller.signal });
    this.refresh();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.observer?.disconnect();
    this.mutations?.disconnect();
    this.reveals?.disconnect();
    this.feeds?.disconnect();
    cancelAnimationFrame(this.scrollFrame);
  }

  refresh() {
    const grid = this.results.querySelector('[ref="grid"]');
    const cards = grid?.querySelectorAll(':scope > [data-product-id][data-page]');
    const lastPage = Number(grid?.dataset.lastPage || 1);
    const lastShown = Number(cards?.[cards.length - 1]?.dataset.page || 1);
    const state = this.results.querySelector('[data-br-load-more]')?.dataset.state;
    const saleBusy = this.results.querySelector('[data-br-sale-filter][aria-busy="true"]');
    this.hidden = Boolean(this.suspended || saleBusy || (state ? state !== 'done' : grid && lastShown < lastPage));
    if (!this.hidden && this.near && !this.loaded && !this.failed) this.start();
    if (!this.hidden && this.loaded) this.queueFeedCheck();
  }

  filter = (event) => {
    if (event.target.closest?.('.shopify-section') !== this.closest('.shopify-section')) return;
    this.suspended = true;
    this.hidden = true;
    const pending = event.promise;
    this.pendingFilter = pending;
    Promise.resolve(pending).then(() => {
      if (!this.isConnected || this.pendingFilter !== pending) return;
      this.suspended = false;
      this.refresh();
    }).catch(() => {});
  };

  async readPage() {
    const url = new URL(this.next, location.origin);
    if (url.origin !== location.origin) throw new Error('Invalid print directory URL');
    url.searchParams.set('section_id', 'br-collection-discovery-data');
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    const request = new AbortController();
    const abort = () => request.abort();
    const signal = this.controller.signal;
    signal.addEventListener('abort', abort, { once: true });
    const timeout = setTimeout(abort, 20000);
    try {
      const response = await fetch(url, { signal: request.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Print directory unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const directory = doc.querySelector('[data-print-directory]');
      if (!directory) throw new Error('Print directory missing');
      for (const link of directory.querySelectorAll('[data-print]')) {
        const href = link.getAttribute('href');
        if (href === this.dataset.current) continue;
        this.records.set(href, { href, name: link.textContent.trim(), image: link.dataset.image,
          Boys: link.dataset.boys === 'true', Girls: link.dataset.girls === 'true' });
      }
      const next = directory.querySelector('[data-next]')?.getAttribute('href');
      if (next === this.next) throw new Error('Print directory pagination repeated');
      this.next = next || '';
    } finally {
      clearTimeout(timeout);
      signal.removeEventListener('abort', abort);
    }
  }

  groupRecords(name) {
    return [...this.records.values()].filter((record) => record[name]);
  }

  queueFeedCheck = () => {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = 0;
      this.checkFeeds();
    });
  };

  checkFeeds() {
    if (!this.isConnected || this.hidden || this.busy || this.suspended) return;
    for (const group of this.groups.values()) {
      if (!group.expanded || group.done || group.failed) continue;
      if (group.feed.getBoundingClientRect().top <= window.innerHeight + 300) {
        this.loadGroup(group);
        break;
      }
    }
  }

  async start() {
    if (this.busy) return;
    this.busy = true;
    this.failed = false;
    this.retry.hidden = true;
    this.status.textContent = 'Loading prints…';
    this.querySelector('.br-discovery__prints').setAttribute('aria-busy', 'true');
    try {
      await this.readPage();
      while (this.next && ['Boys', 'Girls'].some((name) => this.groupRecords(name).length < BATCH)) {
        await this.readPage();
      }
      if (!this.isConnected) return;
      for (const name of ['Boys', 'Girls']) this.createGroup(name);
      this.querySelector('[data-skeleton]').hidden = true;
      for (const group of this.groups.values()) await this.appendCards(group);
      if (!this.isConnected) return;
      this.loaded = true;
      this.status.textContent = this.records.size ? '' : 'No more prints available right now.';
    } catch (error) {
      if (!this.isConnected) return;
      this.failed = true;
      this.status.textContent = 'Prints could not load. Please try again.';
      this.retry.hidden = false;
    } finally {
      this.busy = false;
      this.querySelector('.br-discovery__prints').setAttribute('aria-busy', 'false');
    }
  }

  createGroup(name) {
      if (this.groups.has(name) || (!this.groupRecords(name).length && !this.next)) return;
        const root = document.createElement('section');
        root.className = 'br-discovery__group';
        root.dataset.group = name;
        const heading = document.createElement('h3');
        const label = document.createElement('span');
        label.textContent = name;
        heading.append(label);
        const grid = document.createElement('ul');
        grid.className = 'br-discovery__grid';
        grid.id = `${this.getAttribute('aria-labelledby')}-${name}`;
        const skeleton = this.querySelector(`[data-skeleton] [data-group="${name}"] .br-discovery__grid`);
        if (skeleton) grid.append(...skeleton.children);
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.expand = name;
        button.textContent = `View All ${name} Prints`;
        button.setAttribute('aria-controls', grid.id);
        button.setAttribute('aria-expanded', 'false');
        const feed = document.createElement('div');
        feed.className = 'br-discovery__feed';
        feed.hidden = true;
        feed.dataset.state = 'idle';
        const dots = document.createElement('div');
        dots.className = 'br-discovery__dots';
        dots.setAttribute('aria-hidden', 'true');
        dots.append(...Array.from({ length: 3 }, () => document.createElement('i')));
        feed.append(dots);
        root.append(heading, grid, button, feed);
        this.querySelector('[data-groups]').append(root);
        const group = { name, root, grid, button, feed, count: 0, expanded: false, done: false, failed: false };
        this.groups.set(name, group);
        this.feeds.observe(feed);
  }

  async appendCards(group) {
      const records = this.groupRecords(group.name);
      const next = records.slice(group.count, group.count + BATCH);
      for (let index = 0; index < next.length; index++) {
        if (index % 3 === 0) await frame();
        if (!this.isConnected) return;
        const record = next[index];
        const item = document.createElement('li');
        if (!this.motion.matches) item.classList.add('br-discovery__pending');
        const link = document.createElement('a');
        link.href = record.href;
        const image = document.createElement('img');
        image.alt = '';
        image.width = 360;
        image.height = 360;
        image.loading = 'lazy';
        image.decoding = 'async';
        image.addEventListener('load', () => image.classList.add('is-loaded'), { once: true });
        image.addEventListener('error', () => image.classList.add('is-loaded'), { once: true });
        image.src = record.image;
        const label = document.createElement('span');
        label.textContent = record.name;
        link.append(image, label);
        item.append(link);
        const placeholder = group.grid.children[group.count];
        if (placeholder?.classList.contains('br-discovery__skeleton')) placeholder.replaceWith(item);
        else group.grid.append(item);
        group.count++;
        if (!this.motion.matches) this.reveals.observe(item);
      }
      group.grid.querySelectorAll('.br-discovery__skeleton').forEach((placeholder) => placeholder.remove());
      group.done = group.count >= records.length && !this.next;
      group.button.hidden = group.expanded || group.done;
      group.button.setAttribute('aria-expanded', String(group.expanded));
      group.feed.hidden = !group.expanded;
      // Keep the compact end marker's height so reaching the end never pulls content upward.
      group.feed.dataset.state = group.done ? 'done' : 'idle';
  }

  async loadGroup(group, fromClick = false) {
    if (this.busy || this.hidden || this.suspended || group.done) return;
    this.busy = true;
    group.failed = false;
    group.expanded = true;
    const previousCount = group.count;
    group.button.hidden = true;
    group.feed.hidden = false;
    group.feed.dataset.state = 'loading';
    group.grid.setAttribute('aria-busy', 'true');
    try {
      while (this.next && this.groupRecords(group.name).length < group.count + BATCH) await this.readPage();
      if (!this.isConnected || this.hidden || this.suspended) return;
      await this.appendCards(group);
      this.status.textContent = `${group.count} ${group.name.toLowerCase()} prints shown${group.done ? '. All prints loaded.' : '.'}`;
      if (fromClick) group.grid.children[previousCount]?.querySelector('a').focus({ preventScroll: true });
    } catch (error) {
      if (!this.isConnected) return;
      group.failed = true;
      group.feed.dataset.state = 'error';
      group.button.hidden = false;
      group.button.textContent = 'Try loading more prints';
      this.status.textContent = 'Could not load more prints. Please try again.';
    } finally {
      this.busy = false;
      group.grid.setAttribute('aria-busy', 'false');
      this.queueFeedCheck();
    }
  }

  click = (event) => {
    if (event.target.closest('[data-retry]')) return this.start();
    const button = event.target.closest('[data-expand]');
    if (button) return this.loadGroup(this.groups.get(button.dataset.expand), true);
  };
}

if (!customElements.get('br-collection-discovery')) customElements.define('br-collection-discovery', CollectionDiscovery);
